import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../infrastructure/service/service';
import Box from '@mui/material/Box';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {router} from '../../router/Routes';
import {Button, Card, CardContent, Skeleton, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {TitleTemplate} from '../../../entity/model/title-template';
import {TemplateType} from '../../../entity/enums/template-type';
import LoadingComponent from '../components/widget/LoadingComponent';
import RefreshIcon from '@mui/icons-material/Refresh';
import TemplateDialog from '../components/title-template/TemplateDialog';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {rectSortingStrategy} from '@dnd-kit/sortable';
import {DragContainer} from '../components/widget/DragContainer';
import {KeyValueResource} from '../../../entity/resource/key-value-resource';
import LaunchIcon from '@mui/icons-material/Launch';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/template-details.css';

const TemplateDetails = (): JSX.Element => {
  const {titleTemplateService, countyService, stateService, organizationService} = useService();

  const [loading, setLoading] = useState<boolean>(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false);
  const [titleTemplate, setTitleTemplate] = useState<TitleTemplate>();
  const [titleTemplateDataLeft, setTitleTemplateDataLeft] = useState<KeyValueResource[]>([]);
  const [titleTemplateDataRight, setTitleTemplateDataRight] = useState<KeyValueResource[]>([]);
  const [mode, setMode] = useState<ManageMode>(ManageMode.UPDATE);
  const [isExpanded, setExpanded] = useState<boolean>(true);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      if (!titleTemplateService.getTitleTemplate())
        await router.navigate('/title-bank');

      setTitleTemplate(titleTemplateService.getTitleTemplate());
      await setTemplateHeaderData(titleTemplateService.getTitleTemplate());

      setLoading(false);
    }

    init();
  }, []);

  const setTemplateHeaderData = async (titleTemplate?: TitleTemplate): Promise<void> => {
    const county = await countyService.getCountyById(titleTemplate?.countyID ?? '');
    const state = await stateService.getStateById(titleTemplate?.stateID ?? '');
    const createdOrganization = await organizationService.getOrganizationById(titleTemplate?.createdByOrganizationID ?? '');

    setTitleTemplateDataLeft([
      { label: 'Company', value: createdOrganization?.name ?? '' },
      { label: 'TMS', value: titleTemplate?.tmsParcelNumbers.join(', ') ?? '' },
      { label: 'Type', value: titleTemplate?.isPublic ? TemplateType.PUBLIC : TemplateType.ORGANIZATION },
      { label: 'Sub Division', value: titleTemplate?.subdivision ?? '' },
      { label: 'County', value: county?.name },
      { label: 'State', value: state?.shortName }
    ]);

    setTitleTemplateDataRight([
      { label: 'Created', value: titleTemplate ? new Date(titleTemplate.createdAt).toDateString() : '-'},
      { label: 'Updated', value: titleTemplate ? new Date(titleTemplate.updatedAt).toDateString() : '-'}
    ]);
  }

  const refreshPage = async (): Promise<void> => {
    setLoading(true);

    const updatedTemplate = await titleTemplateService.getTitleTemplateById(titleTemplate?.createdByOrganizationID ?? '', titleTemplate?.id ?? '', true);
    setTitleTemplate(updatedTemplate);

    if (updatedTemplate)
      await setTemplateHeaderData(updatedTemplate);

    setLoading(false);
  }

  const handleBackBtn = async (): Promise<void> => {
    titleTemplateService.enabledTitleBankCache();
    await router.navigate('/title-bank');
  }

  const handleManageTemplateDialog = (manageMode: ManageMode): void => {
    setOpenTemplateDialog(true);
    setMode(manageMode);
  }

  const handleExpandDetails = (): void => {
    setExpanded((prevState: boolean): boolean => !prevState);
  }

  return (
    <>
      {
        loading &&
          <LoadingComponent/>
      }
      {
        titleTemplate && !loading &&
          <Box>
            <Box display={'flex'}>
              <Box display={'flex'}>
                <ArrowBackIcon className={'breadcrumb-icon'} onClick={handleBackBtn}/>
                <Typography className={'breadcrumb-title'}>{`Template Name #${titleTemplate.name}`}</Typography>
                <Typography className={'breadcrumb-header-expand-text template'} onClick={handleExpandDetails}>
                  {isExpanded ? 'Hide Details' : 'Show Details'}
                </Typography>
              </Box>
              <Box className={'breadcrumb-btn-container'}>
                <Button
                  className={'zitles-button stats-toolbar-button refresh'}
                  startIcon={<RefreshIcon/>}
                  disableTouchRipple
                  onClick={refreshPage}>
                  Refresh
                </Button>
                <Button
                  className={'zitles-button stats-toolbar-button end center'}
                  onClick={(): void => handleManageTemplateDialog(ManageMode.UPDATE)}>
                  Edit Details
                </Button>
                <Button
                  className={'zitles-button stats-toolbar-button end'}
                  onClick={(): void => handleManageTemplateDialog(ManageMode.DOWNLOAD)}>
                  Download Template
                </Button>
              </Box>
            </Box>
            <Card className={'details-card single'} elevation={0}>
              <CardContent>
                <Box className={'details-card-header-container'}>
                  <Typography className={'details-card-header'}>Details</Typography>
                  <LaunchIcon className={'zitles-icon'}/>
                </Box>
                {
                  isExpanded &&
                    <Grid container spacing={2}>
                      <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 6}}>
                        <Grid container className={'details-card-body primary'} spacing={2}>
                          {
                            titleTemplateDataLeft.map((data: KeyValueResource, index: number): JSX.Element =>
                              <Grid size={6} key={index}>
                                <Box className={'details-card-item'}>
                                  <Typography className={'details-card-item-label'}>{data.label}</Typography>
                                  <Typography className={'details-card-item-value'}>{data.value ?? <Skeleton/>}</Typography>
                                </Box>
                              </Grid>
                            )
                          }
                        </Grid>
                      </Grid>
                      <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 6}}>
                        <Grid container className={'details-card-body'} spacing={2}>
                          {
                            titleTemplateDataRight.map((data: KeyValueResource, index: number): JSX.Element =>
                                <Grid size={12} key={index}>
                                  <Box className={`details-card-item ${index === 0 ? 'first' : ''}`}>
                                    <Typography className={'details-card-item-label'}>{data.label}</Typography>
                                    <Typography className={'details-card-item-value link'}>{data.value ?? <Skeleton/>}</Typography>
                                  </Box>
                                </Grid>
                            )
                          }
                        </Grid>
                      </Grid>
                    </Grid>
                }
              </CardContent>
            </Card>
            <DragContainer
              strategy={rectSortingStrategy}
              multiSelect
              scrollable/>
          </Box>
      }
      {
        openTemplateDialog &&
          <TemplateDialog
            open={openTemplateDialog}
            setOpen={setOpenTemplateDialog}
            mode={mode}
            titleTemplate={titleTemplateService.getTitleTemplate()}
            reloadCallback={refreshPage}
            orderNumber={''}/>
      }
    </>
  );
}

export default observer(TemplateDetails);
